import {
  useEncounters,
} from 'pages/Dashboard/pages/Encounters/pages/List/hooks';
import {
  useEncounterFilterMeta,
} from 'pages/Dashboard/pages/Encounters/hooks';
import useAppointmentsSettings, {
  preAppointmentStatusMapping,
} from 'pages/Dashboard/hooks/useAppointmentsSettings';
import {
  Appointment,
} from 'pages/Dashboard/pages/Appointments/pages/List/types/event';
import {
  FieldAttributes,
  FilterObjectType,
  FilterableSettings,
  ObjectAttributes,
  ObjectType,
} from 'core/Filters/types';
import {
  useAppointments,
} from 'pages/Dashboard/pages/Appointments/pages/List/hooks';
import {
  defaultComparator,
} from 'core/Filters/context';
import {
  DecimalNullableRangeFilterDTO,
} from 'dtos/decimalNullableRangeFilterDTO';
import {
  FilterableSettingDTO,
} from 'dtos/filterableSettingDTO';
import {
  useChargesFilterMetaComponents,
} from 'pages/Dashboard/pages/Billing/Charges/hooks';
import {
  useToDosFilterMetadata,
} from 'pages/Dashboard/pages/Todos/hooks';
import {
  useEOBFilterMetaComponents,
  useGetPatientPaymentFilterSettings,
} from 'pages/Dashboard/pages/Billing/hooks';
import {
  useGetEFaxFilterMetaComponents,
} from 'pages/Dashboard/pages/Fax/hooks';
import {
  EncounterViewModel,
} from 'dtos';
import {
  Dayjs,
} from 'dayjs';
import {
  useGetPatientMultimediaFilterMetaComponents,
} from 'pages/Dashboard/pages/Charts/pages/Details/hooks';
import {
  useRTMMonitoringFilterMetaComponents,
} from 'pages/Dashboard/pages/Dashboard/RTM/hooks';
import {
  useCcmMonthlyDashboardFilterMetaComponents,
} from 'pages/Dashboard/pages/Dashboard/CcmMonthly/hooks';
import {
  useTempoDashboardFilterMetaComponents,
} from 'pages/Dashboard/pages/Dashboard/Tempo/hooks';
import {
  useFormWorkflowFilterMetadata,
} from 'pages/Dashboard/pages/Forms/pages/FormWorkflow/hooks';
import {
  displayNameKey,
} from 'types/nameFormat';

type PatientFilterAppointment = {
  canBeRescheduledSooner?: boolean;
  intakeFormCompleted?: boolean;
  consentStatus?: string;
};

export function matchesPatientPreAppointmentStatus(
  filterValues: number[],
  appointment: PatientFilterAppointment,
  viewType?: string,
): boolean {
  if (viewType === 'Monthly') return true;

  const rescheduledFilters: boolean[] = [];
  const intakeAndConsentFilters: boolean[] = [];

  filterValues.forEach((valueId) => {
    const selectedStatus = preAppointmentStatusMapping[valueId];
    if (selectedStatus.isSchedule) {
      rescheduledFilters.push(selectedStatus.value as boolean);
    } else {
      intakeAndConsentFilters.push(selectedStatus.value as boolean);
    }
  });

  const hasCompletedIntakeAndConsent = appointment.intakeFormCompleted === true
    && ['Given', 'NotRequired'].includes(appointment.consentStatus ?? '');

  return rescheduledFilters.includes(appointment.canBeRescheduledSooner ?? false)
    || intakeAndConsentFilters.includes(hasCompletedIntakeAndConsent);
}

const serviceFacilityFilter: FieldAttributes = {
  label: 'Facility',
  allCaption: 'All Facilities',
  fieldValueKey: 'serviceFacilityId',
  fieldLabelKey: 'nickname',
  defaultValue: [],
  type: 'multiselect',
  comparator: (
    filterValues,
    object,
    filterKey,
    [, viewType] = [],
  ) => viewType === 'Monthly' || filterValues.some(
    (id) => object?.serviceFacility?.serviceFacilityId === id || object?.serviceFacilityId === id,
  ),
};

// @TODO
// when it will be over 3 objects then
// move each object attributes definition to a separate file
export const objectAttributesMapping: Record<ObjectType, ObjectAttributes> = {
  encounter: {
    getData: useEncounters,
    getMetaData: useEncounterFilterMeta,
    getObjectAccessor: (encounter: EncounterViewModel) => encounter,
    normalizeMetaData: (meta: Record<string, any[]>) => ({
      userId: meta.doctors ?? [],
      typeOfVisitDescription: meta.appointmentTypes,
      patientAppointmentStatus: meta.appointmentStatuses ?? [],
      serviceFacility: meta.serviceFacility ?? [],
    }),
    fields: {
      userId: {
        defaultValue: [],
        label: 'Doctors',
        allCaption: 'All Doctors',
        fieldValueKey: 'userId',
        fieldLabelKey: displayNameKey,
        type: 'multiselect',
      },
      typeOfVisitDescription: {
        label: 'Visit Types',
        allCaption: 'All Visits',
        defaultValue: [],
        type: 'multiselect',
      },
      patientAppointmentStatus: {
        label: 'Appointment Statuses',
        allCaption: 'All Appointments',
        defaultValue: [],
        fieldValueKey: 'patientAppointmentStatus',
        fieldLabelKey: 'displayName',
        type: 'multiselect',
      },
      serviceFacility: serviceFacilityFilter,
    },
  },
  appointment: {
    getData: useAppointments,
    getMetaData: useAppointmentsSettings,
    getObjectAccessor: (appointment: Appointment) => appointment,
    normalizeMetaData: (meta: Record<string, any[]>) => ({
      userId: meta.doctors ?? [],
      typeOfVisitDescription: meta.appointmentTypes ?? [],
      patientAppointmentStatus: meta.appointmentStatuses ?? [],
      patientPreAppointmentStatus: meta.patientPreAppointmentStatus ?? [],
      serviceFacility: meta.serviceFacility ?? [],
      patientAlertTypes: meta.patientAlertTypes ?? [],
    }),
    fields: {
      userId: {
        defaultValue: [],
        label: 'Doctors',
        allCaption: 'All Doctors',
        fieldValueKey: 'userId',
        fieldLabelKey: displayNameKey,
        type: 'multiselect',
      },
      typeOfVisitDescription: {
        label: 'Appointment Types',
        allCaption: 'All Appointments',
        defaultValue: [],
        type: 'multiselect',
      },
      patientAppointmentStatus: {
        label: 'Appointment Statuses',
        allCaption: 'All Visits',
        defaultValue: [],
        fieldValueKey: 'patientAppointmentStatus',
        fieldLabelKey: 'displayName',
        type: 'multiselect',
        comparator: (filterValues, object, filterKey, [, viewType] = []) => {
          if (viewType === 'Monthly') return true;
          return defaultComparator(filterValues, object, filterKey);
        },
      },
      patientPreAppointmentStatus: {
        defaultValue: [],
        label: 'Patient Filter',
        allCaption: 'All Patients',
        fieldValueKey: 'id',
        fieldLabelKey: 'caption',
        comparator: (filterValues, object, _filterKey, [, viewType] = []) => (
          matchesPatientPreAppointmentStatus(filterValues, object, viewType)
        ),
        type: 'multiselect',
      },
      patientAlertTypes: {
        defaultValue: [],
        label: 'Patient Alerts',
        allCaption: 'All Alerts',
        fieldValueKey: 'type',
        fieldLabelKey: 'label',
        type: 'multiselect',
        comparator: (filterValues, object, _filterKey, [, viewType] = []) => {
          if (viewType === 'Monthly') return true;

          const patientAlerts = object.patientAlerts ?? [];
          return patientAlerts.some((alert: { type?: string }) => (
            filterValues.includes(alert.type)
          ));
        },
      },
      serviceFacility: serviceFacilityFilter,
    },
  },
};

export type FilterAttributes = {
  getComponents: () => FilterableSettings[];
};

export const filterMap: Record<FilterObjectType, FilterAttributes> = {
  charges: {
    getComponents: useChargesFilterMetaComponents,
  },
  EOBs: {
    getComponents: useEOBFilterMetaComponents,
  },
  toDos: {
    getComponents: useToDosFilterMetadata,
  },
  patientPaymentsTransactions: {
    getComponents: useGetPatientPaymentFilterSettings,
  },
  eFax: {
    getComponents: useGetEFaxFilterMetaComponents,
  },
  patientMultimedia: {
    getComponents: useGetPatientMultimediaFilterMetaComponents,
  },
  rtmMonitoring: {
    getComponents: useRTMMonitoringFilterMetaComponents,
  },
  tempoDashboard: {
    getComponents: useTempoDashboardFilterMetaComponents,
  },
  ccmMonthlyDashboard: {
    getComponents: useCcmMonthlyDashboardFilterMetaComponents,
  },
  formWorkflows: {
    getComponents: useFormWorkflowFilterMetadata,
  },
};

export const filterComponentsByType = (objectType: FilterObjectType) => {
  const { getComponents } = filterMap[objectType];
  return getComponents();
};

export type MergedFilterValueType =
  DecimalNullableRangeFilterDTO
  & FilterableSettingDTO
  & Dayjs;
